import { useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChatCircleTextIcon, XIcon } from "@phosphor-icons/react";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useCommentStore } from "../model/store/commentsStore";
import { useCommentAnchorsStore } from "../model/store/commentAnchorsStore";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { getMarkedText, removeMark } from "../lib/lib";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { Comment } from "./Comment";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { CommentInputField } from "./_CommentInputField";

export function CommentsIsland() {
  const { id } = useParams();
  const { t } = useTranslation(["projectHome", "common"]);

  const scrollableCommentsRef = useRef<HTMLDivElement>(null);

  // Tech debt: move to chapter entity
  const { activeChapter } = useChapterStore();
  // Tech debt: move to user entity
  const { firstName, lastName, id: userId } = useMeStore();
  const { isFetchProjectLoading } = useProjectStore();
  const { getComments, isLoadingComments, getCommentsError, fetchComments, deleteComment } = useCommentStore();
  const { setScrollToBottomCallback, closeHandler } = useProjectSidebarStore();
  const { commentsUpdated, removeCommentMark } = useEditorStore();
  const anchorEntries = useCommentAnchorsStore((s) => s.entries);

  const projectId = id || "";
  const chapterId = activeChapter?.id?.toString() || "";
  const comments = getComments(chapterId);
  const isCommentsLoading = isLoadingComments(chapterId);
  const commentsError = getCommentsError(chapterId);

  useEffect(() => {
    if (projectId && chapterId) fetchComments(projectId, chapterId);
  }, [projectId, chapterId, fetchComments]);

  const scrollToBottom = () => {
    if (scrollableCommentsRef.current) {
      scrollableCommentsRef.current.scrollTo({ top: scrollableCommentsRef.current.scrollHeight, behavior: "smooth" });
    }
  };

  useEffect(() => {
    setScrollToBottomCallback(scrollToBottom);

    return () => {
      setScrollToBottomCallback(null);
    };
  }, [setScrollToBottomCallback]);

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!projectId || !chapterId) return;
      await deleteComment(projectId, chapterId, commentId);
      removeMark(commentId);
      removeCommentMark?.(commentId);
      commentsUpdated?.();
    },
    [projectId, chapterId, removeCommentMark, commentsUpdated, deleteComment]
  );

  const isLoading = isCommentsLoading || isFetchProjectLoading;
  const isEmpty = !commentsError && !isLoading && comments.length === 0;

  return (
    <div className="flex h-full w-full flex-col bg-card" data-testid="comments-root">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-foreground/5 ps-4 pe-2">
        <h3 className="typo-label-md text-foreground">{t("comments.title")}</h3>
        {comments.length > 0 && (
          <Badge variant="secondary" className="h-5 min-w-5 px-1.5 tabular-nums">
            {comments.length}
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          shape="pill"
          aria-label={t("comments.close")}
          data-testid="comments-close-button"
          onClick={closeHandler}
          className="ms-auto text-muted-foreground"
        >
          <XIcon />
        </Button>
      </header>
      <div ref={scrollableCommentsRef} className="min-h-0 flex-1 overflow-y-auto p-3" data-testid="comments-scrollable">
        {isLoading && (
          <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
            <Spinner aria-label={t("common:loading")} />
            <span className="typo-caption">{t("comments.loading")}</span>
          </div>
        )}

        {(isEmpty || commentsError) && !isLoading && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <span className="flex size-10 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <ChatCircleTextIcon weight="duotone" className="size-5" />
            </span>
            <span className="typo-label-md text-foreground">{t("comments.emptyTitle")}</span>
            <span className="typo-caption text-muted-foreground">{t("comments.emptyDescription")}</span>
          </div>
        )}

        {!isLoading && userId && (
          <div className="flex flex-col gap-2">
            {comments.map((comment) => {
              const markedText =
                getMarkedText(comment.id) ||
                anchorEntries.find((entry) => entry.commentId === comment.id)?.text ||
                comment.anchor?.quote ||
                "";
              return (
                <Comment key={comment.id} comment={comment} onDelete={handleDeleteComment} markedText={markedText} />
              );
            })}
          </div>
        )}
      </div>

      {userId && (
        <CommentInputField
          onSubmit={scrollToBottom}
          isLoading={isCommentsLoading}
          userId={userId}
          firstName={firstName}
          lastName={lastName}
        />
      )}
    </div>
  );
}
